/* Verify email, the link-callback fallback, sign in, the two-step code and password reset.
   After a successful call these pages only update the session: the access guard then sends the student on
   (to ?next=, their account, or the guardian page). */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { WEB_ROUTES, type Me } from '@pathabroad/core/browser';
import { useT } from '../app/i18n';
import { Link, navigate, replaceUrl, useLocation } from '../app/nav';
import { useSession } from '../app/session';
import { useErrorHandler, useNow, focusHeading } from '../app/hooks';
import { signupMemory } from '../app/memory';
import { Alert, Button, CodeField, Live, Loading, PageHeader, PasswordField, TextField } from '../app/ui';
import { api, ApiError } from '../lib/api';
import { clock } from '../lib/format';
import { looksLikeEmail, MIN_PASSWORD, sixDigits } from '../lib/forms';
import { homeFor } from '../lib/routes';
import { useSignupStep } from './Start';

const RESEND_SECONDS = 30;

/* ---------- /verify-email ---------- */
export function VerifyEmail() {
  const { t } = useT();
  const { setMe } = useSession();
  const handle = useErrorHandler();
  const pending = signupMemory.get();
  const step = useSignupStep(3, pending?.planner);
  const next = useLocation().query.get('next');
  const [email, setEmail] = useState(pending?.email ?? '');
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState<{ email?: string; code?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);
  const [resendAt, setResendAt] = useState(0);
  const [resendMsg, setResendMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const now = useNow(resendAt > Date.now());
  const wait = Math.max(0, Math.ceil((resendAt - now) / 1000));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const errs: typeof errors = {};
    if (!pending && !looksLikeEmail(email)) errs.email = t('v.email');
    if (code.length !== 6) errs.code = t('v.code');
    setErrors(errs);
    if (errs.email || errs.code) { document.getElementById(errs.email ? 've-email' : 've-code')?.focus(); return; }
    setBusy(true);
    try {
      const r = await api.post<{ me: Me }>('/api/auth/verify-email', { email: email.trim(), code });
      signupMemory.clear();
      setMe(r.me);
    } catch (err) {
      setBusy(false);
      const msg = handle(err);
      if (msg) setErrors({ form: msg });
    }
  };

  const resend = async () => {
    if (!pending || wait > 0) return;
    setResendMsg(null);
    try {
      await api.post('/api/auth/signup', { email: pending.email, password: pending.password, dob: pending.dob, lang: pending.lang });
      setResendMsg({ tone: 'success', text: t('verify.resent', { email: pending.email }) });
      setResendAt(Date.now() + RESEND_SECONDS * 1000);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'rate_limited') {
        setResendMsg({ tone: 'error', text: t('verify.rateLimited') });
        setResendAt(Date.now() + 5 * 60 * 1000);
        return;
      }
      const msg = handle(err);
      if (msg) setResendMsg({ tone: 'error', text: msg });
    }
  };

  return (
    <div className="narrow">
      <PageHeader title={t('verify.title')} eyebrow={step} lead={pending ? t('verify.sent', { email: pending.email }) : t('verify.noEmail')} />
      {pending?.minor && <div className="mb"><Alert tone="info">{t('start.form.minor')}</Alert></div>}
      <form className="card stack" noValidate onSubmit={submit} aria-labelledby="page-title">
        {!pending && (
          <TextField id="ve-email" label={t('common.email')} type="email" autoComplete="email" inputMode="email" ltr
            autoCapitalize="none" spellCheck={false} value={email} onChange={setEmail} error={errors.email} />
        )}
        <CodeField id="ve-code" label={t('verify.code')} value={code} onChange={v => setCode(sixDigits(v))} error={errors.code} autoFocus={!!pending} />
        {errors.form && <Alert>{errors.form}</Alert>}
        <Button type="submit" block busy={busy}>{t('verify.submit')}</Button>
      </form>
      <div className="aside stack-sm">
        {pending ? (
          <button type="button" className="linkbtn" onClick={resend} aria-disabled={wait > 0 || undefined}>
            {wait > 0 ? t('verify.resendIn', { time: clock(wait) }) : t('verify.resend')}
          </button>
        ) : (
          <p>{t('verify.noResend')}</p>
        )}
        <Live>{resendMsg && <span className={resendMsg.tone === 'error' ? 'text-danger' : undefined}>{resendMsg.text}</span>}</Live>
        <p><Link to={next ? `${WEB_ROUTES.start}?next=${encodeURIComponent(next)}` : WEB_ROUTES.start}>{t('verify.startAgain')}</Link></p>
      </div>
    </div>
  );
}

/* ---------- /auth/callback: tokens in the #fragment from an email link ---------- */
export function AuthCallback() {
  const { t } = useT();
  const { setMe } = useSession();
  const [failed, setFailed] = useState(false);
  const handled = useRef(false);
  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    const params = new URLSearchParams(location.hash.replace(/^#/, ''));
    /* take the tokens out of the address bar (and history) straight away */
    replaceUrl(location.pathname + location.search);
    const access = params.get('access_token'), refresh = params.get('refresh_token');
    if (!access || !refresh || params.get('error')) { setFailed(true); return; }
    api.post<{ me: Me }>('/api/auth/session', { access_token: access, refresh_token: refresh })
      .then(r => {
        setMe(r.me);
        navigate(params.get('type') === 'recovery' && homeFor(r.me) === WEB_ROUTES.planner ? WEB_ROUTES.security : homeFor(r.me), { replace: true });
      })
      .catch(() => setFailed(true));
  }, [setMe]);
  return (
    <div className="narrow">
      <PageHeader title={t('callback.title')} />
      {failed
        ? <><Alert>{t('callback.failed')}</Alert><p className="aside"><Link to={WEB_ROUTES.signin}>{t('callback.signin')}</Link></p></>
        : <Loading label={t('callback.working')} />}
    </div>
  );
}

/* ---------- /signin ---------- */
export function SignIn() {
  const { t } = useT();
  const { setMe, refresh } = useSession();
  const handle = useErrorHandler();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string; form?: string; unconfirmed?: boolean }>({});
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const errs: typeof errors = {};
    if (!looksLikeEmail(email)) errs.email = t('v.email');
    if (!password) errs.password = t('v.required');
    setErrors(errs);
    if (errs.email || errs.password) { document.getElementById(errs.email ? 'si-email' : 'si-password')?.focus(); return; }
    setBusy(true);
    try {
      const r = await api.post<{ mfa_required: boolean; me?: Me }>('/api/auth/signin', { email: email.trim(), password });
      if (r.mfa_required || !r.me) await refresh();   /* signed in at aal1: the guard asks for the code next */
      else setMe(r.me);
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError && err.code === 'email_not_confirmed') { setErrors({ form: t('err.email_not_confirmed'), unconfirmed: true }); return; }
      const msg = handle(err);
      if (msg) setErrors({ form: msg });
    }
  };

  return (
    <div className="narrow">
      <PageHeader title={t('signin.title')} eyebrow={t('auth.welcomeBack')} />
      <form className="card stack" noValidate onSubmit={submit} aria-labelledby="page-title">
        <TextField id="si-email" label={t('common.email')} type="email" autoComplete="username" inputMode="email" ltr
          autoCapitalize="none" spellCheck={false} value={email} onChange={setEmail} error={errors.email} />
        <PasswordField id="si-password" label={t('common.password')} value={password} onChange={setPassword}
          autoComplete="current-password" error={errors.password} />
        {errors.form && (
          <Alert>{errors.form}{errors.unconfirmed && <> <Link to={WEB_ROUTES.verifyEmail}>{t('signin.enterCode')}</Link></>}</Alert>
        )}
        <Button type="submit" block busy={busy}>{t('signin.title')}</Button>
        <p className="center"><Link to={WEB_ROUTES.resetPassword}>{t('signin.forgot')}</Link></p>
      </form>
      <p className="aside center">{t('signin.new')} <Link to={WEB_ROUTES.start}>{t('signin.create')}</Link></p>
    </div>
  );
}

/* ---------- /signin/code: the two-step code ---------- */
export function SignInCode() {
  const { t } = useT();
  const { setMe, signOut } = useSession();
  const handle = useErrorHandler();
  const [code, setCode] = useState('');
  const [error, setError] = useState<{ code?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (code.length !== 6) { setError({ code: t('v.code') }); document.getElementById('mfa-code')?.focus(); return; }
    setBusy(true);
    try {
      const r = await api.post<{ me: Me }>('/api/auth/mfa/verify', { code });
      setMe(r.me);
    } catch (err) {
      setBusy(false);
      setCode('');
      const msg = handle(err, ['mfa_required']);
      if (msg) setError({ form: msg });
    }
  };
  return (
    <div className="narrow">
      <PageHeader title={t('code.title')} lead={t('code.lead')} />
      <form className="card stack" noValidate onSubmit={submit} aria-labelledby="page-title">
        <CodeField id="mfa-code" label={t('code.label')} value={code} onChange={v => setCode(sixDigits(v))} error={error.code} autoFocus />
        {error.form && <Alert>{error.form}</Alert>}
        <Button type="submit" block busy={busy}>{t('common.continue')}</Button>
      </form>
      <div className="aside stack-sm">
        <p>{t('code.help', { email: t('company.supportEmail') })}</p>
        <p><button type="button" className="linkbtn" onClick={() => void signOut()}>{t('code.switch')}</button></p>
      </div>
    </div>
  );
}

/* ---------- /reset-password: ask for a code, then the code and a new password ---------- */
export function ResetPassword() {
  const { t } = useT();
  const { setMe } = useSession();
  const handle = useErrorHandler();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; code?: string; password?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);
  const [resendAt, setResendAt] = useState(0);
  const now = useNow(resendAt > Date.now());
  const wait = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const first = useRef(true);
  useEffect(() => { if (first.current) { first.current = false; return; } focusHeading(); }, [step]);

  const sendCode = async () => {
    setBusy(true);
    try {
      await api.post('/api/auth/password-reset', { email: email.trim() });
      setStep('code');
      setResendAt(Date.now() + RESEND_SECONDS * 1000);
    } catch (err) {
      const msg = handle(err);
      if (msg) setErrors({ form: msg });
    } finally { setBusy(false); }
  };

  const submitEmail = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!looksLikeEmail(email)) { setErrors({ email: t('v.email') }); document.getElementById('rp-email')?.focus(); return; }
    setErrors({});
    void sendCode();
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const errs: typeof errors = {};
    if (code.length !== 6) errs.code = t('v.code');
    if ([...password].length < MIN_PASSWORD) errs.password = t('v.password');
    setErrors(errs);
    if (errs.code || errs.password) { document.getElementById(errs.code ? 'rp-code' : 'rp-password')?.focus(); return; }
    setBusy(true);
    try {
      const r = await api.post<{ me: Me }>('/api/auth/password-reset/confirm', { email: email.trim(), code, password });
      setMe(r.me);
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError && err.code === 'invalid' && err.field === 'password') { setErrors({ password: t('v.password') }); return; }
      const msg = handle(err);
      if (msg) setErrors({ form: msg });
    }
  };

  if (step === 'email') {
    return (
      <div className="narrow">
        <PageHeader title={t('reset.title')} lead={t('reset.lead')} />
        <form className="card stack" noValidate onSubmit={submitEmail} aria-labelledby="page-title">
          <TextField id="rp-email" label={t('common.email')} type="email" autoComplete="username" inputMode="email" ltr
            autoCapitalize="none" spellCheck={false} value={email} onChange={setEmail} error={errors.email} />
          {errors.form && <Alert>{errors.form}</Alert>}
          <Button type="submit" block busy={busy}>{t('reset.send')}</Button>
        </form>
        <p className="aside center"><Link to={WEB_ROUTES.signin}>{t('reset.back')}</Link></p>
      </div>
    );
  }
  return (
    <div className="narrow">
      <PageHeader title={t('reset.title')} lead={t('reset.sent', { email: email.trim() })} />
      <form className="card stack" noValidate onSubmit={submitCode} aria-labelledby="page-title">
        <CodeField id="rp-code" label={t('verify.code')} value={code} onChange={v => setCode(sixDigits(v))} error={errors.code} />
        <PasswordField id="rp-password" label={t('reset.newPassword')} value={password} onChange={setPassword}
          autoComplete="new-password" showStrength error={errors.password} />
        {errors.form && <Alert>{errors.form}</Alert>}
        <Button type="submit" block busy={busy}>{t('reset.submit')}</Button>
      </form>
      <div className="aside stack-sm">
        <button type="button" className="linkbtn" aria-disabled={wait > 0 || busy || undefined} onClick={() => { if (wait === 0 && !busy) void sendCode(); }}>
          {wait > 0 ? t('verify.resendIn', { time: clock(wait) }) : t('verify.resend')}
        </button>
        <p><button type="button" className="linkbtn" onClick={() => { setStep('email'); setCode(''); setPassword(''); setErrors({}); }}>{t('reset.otherEmail')}</button></p>
      </div>
    </div>
  );
}
